import { getDisplayAssetCode } from '@altify/utils';
import { getCurrencyTypeLabel } from 'utils/currencies';
import {
  getAmountChangeForTimeFrameKey,
  getPercentageChangeForTimeFrameKey,
} from 'app/_authenticated/products/explore/components/ExploreGainLoss';
import { ProductsWithHoldingsAndRates } from 'hooks/products/useProducts';
import { useProductsGraphs } from 'hooks/products/useGraphs';
import { BundleInfo } from 'store/slices/types/BundleInfo';
import { Rate } from 'store/slices/types/Rate';
import { ActiveProductDtoTypeEnum, BreakdownItemDto } from 'services/generated';

/**
 * Builds a list of available assets with their current data
 */
export const buildAvailableAssets = (
  products: ProductsWithHoldingsAndRates | null,
  bundlesInfo: BundleInfo[] | undefined,
  rates: Record<string, Rate> | undefined,
  productGraph: ReturnType<typeof useProductsGraphs>,
  currencyCode: string,
) => {
  return (
    products
      ?.filter(
        p =>
          p.type !== ActiveProductDtoTypeEnum.Locked &&
          p.type !== ActiveProductDtoTypeEnum.Fiat &&
          p.isTracked,
      )
      ?.map(p => ({
        assetCode: getDisplayAssetCode(p.code) ?? '',
        assetName: p.name,
        type: getCurrencyTypeLabel(p.code, p.type, 'en'),
        price: p.rate ?? 0,
        returnValue:
          p.type === ActiveProductDtoTypeEnum.PrivateCredit
            ? 0
            : (getAmountChangeForTimeFrameKey(p.code, productGraph) ?? 0),
        returnPercentage:
          p.type === ActiveProductDtoTypeEnum.PrivateCredit
            ? 11
            : (getPercentageChangeForTimeFrameKey(p.code, productGraph) ?? 0),
        breakdown:
          p.type === ActiveProductDtoTypeEnum.Bundle
            ? getBreakDownInfo(
                bundlesInfo || [],
                products,
                rates ?? {},
                p.code,
                currencyCode,
              )
            : [],
      })) ?? []
  );
};

/**
 * Builds asset breakdown for portfolio analysis
 */
export const buildAssetBreakdown = (
  products: ProductsWithHoldingsAndRates | null,
) => {
  return (
    products
      ?.filter(
        p => p.type !== ActiveProductDtoTypeEnum.Locked && p.totalAmount > 0,
      )
      ?.map(p => ({
        assetCode: getDisplayAssetCode(p.code) ?? '',
        assetValue: p.totalAmountInDisplay ?? 0,
        allTimeReturnValue:
          p.type === ActiveProductDtoTypeEnum.Fiat ||
          p.type === ActiveProductDtoTypeEnum.PrivateCredit
            ? 0
            : (p.unrealizedGainLossAmount ?? 0),
        allTimeReturnPercentage:
          p.type === ActiveProductDtoTypeEnum.Fiat
            ? 0
            : (p.unrealizedGainLossPercentage ?? 0),
        weighting: p.breakdownPercentage ?? 0,
      })) ?? []
  );
};

/**
 * Builds portfolio summary with total values and returns
 */
export const buildPortfolioSummary = (
  products: ProductsWithHoldingsAndRates | null,
  gainsAndLosses: { gainPercentage: number; gainAmount: number } | undefined,
) => {
  const totalPortfolioValue = products?.reduce(
    (acc, curr) => acc + (curr.totalAmountInDisplay ?? 0),
    0,
  );

  return {
    portfolioValue: totalPortfolioValue ?? 0,
    portfolioReturnPercentage: gainsAndLosses?.gainPercentage ?? 0,
    portfolioReturnValue: gainsAndLosses?.gainAmount ?? 0,
  };
};

/**
 * Gets breakdown information for bundle products
 */
const getBreakDownInfo = (
  bundlesInfo: BundleInfo[],
  products: ProductsWithHoldingsAndRates,
  rates: Record<string, Rate>,
  productCode: string,
  functionalCurrency: string,
): BreakdownItemDto[] => {
  const breakDownInfo =
    bundlesInfo
      .find(b => b.code === productCode)
      ?.assets.map(a => {
        const product = products.find(prod => prod.code === a.code);
        return {
          assetCode: a.code,
          assetName: product?.name || '',
          currentWeighting: a.currentWeight,
          price:
            product?.rate ??
            getPriceForProduct(a.price, rates, functionalCurrency),
          returnValue: product?.unrealizedGainLossAmount ?? 0,
          returnPercentage: product?.unrealizedGainLossPercentage ?? 0,
        };
      }) ?? [];
  return breakDownInfo;
};

/**
 * Gets the price for a product in the functional currency
 */
const getPriceForProduct = (
  usdPrice: number,
  rates: Record<string, Rate>,
  currencyCode: string,
) => {
  const market = 'USDT_' + currencyCode;
  const currencyRate = rates[market]?.price || 1;
  return currencyRate * usdPrice;
};
